import React, { useState, useEffect } from 'react';
import { Monitor, Globe, Save, Eye, EyeOff, Link } from 'lucide-react';
import apiService from '../../services/apiService';
import { useApp } from '../../context/AppContext';
import Button from '../ui/Button';

// Todos los views disponibles en el sistema con sus labels
const ALL_VIEWS = [
    { id: 'ventas-mostrador', label: 'Ventas de Mostrador', group: 'Ventas', defaultWeb: false },
    { id: 'corte-caja', label: 'Corte de Caja', group: 'Ventas', defaultWeb: false },
    { id: 'pedidos', label: 'Pedidos', group: 'Ventas', defaultWeb: true },
    { id: 'cuentas-por-cobrar', label: 'Cuentas por Cobrar', group: 'Ventas', defaultWeb: true },
    { id: 'devoluciones', label: 'Devoluciones', group: 'Ventas', defaultWeb: false },
    { id: 'ordenes-compra', label: 'Órdenes de Compra', group: 'Compras', defaultWeb: true },
    { id: 'cuentas-por-pagar', label: 'Cuentas por Pagar', group: 'Compras', defaultWeb: true },
    { id: 'almacenes', label: 'Almacenes', group: 'Catálogos', defaultWeb: true },
    { id: 'products', label: 'Productos', group: 'Catálogos', defaultWeb: true },
    { id: 'migracion-productos', label: 'Migrar Productos', group: 'Catálogos', defaultWeb: true },
    { id: 'cambio-precios', label: 'Cambio de Precios', group: 'Catálogos', defaultWeb: true },
    { id: 'precios-ray', label: 'Precios Ray', group: 'Catálogos', defaultWeb: true },
    { id: 'etiquetas-zebra', label: 'Etiquetas Zebra', group: 'Catálogos', defaultWeb: false },
    { id: 'customers', label: 'Clientes', group: 'Catálogos', defaultWeb: true },
    { id: 'proveedores', label: 'Proveedores', group: 'Catálogos', defaultWeb: true },
    { id: 'picking', label: 'Picking', group: 'Almacén', defaultWeb: false },
    { id: 'preparacion', label: 'Preparación', group: 'Almacén', defaultWeb: false },
    { id: 'medido-hoy', label: 'Pedidos de Hoy', group: 'Logística', defaultWeb: true },
    { id: 'caratula-pedidos', label: 'Carátula de Pedidos', group: 'Logística', defaultWeb: true },
    { id: 'flete', label: 'Gestión de Flete', group: 'Logística', defaultWeb: true },
    { id: 'facturacion', label: 'Facturación', group: 'Facturación', defaultWeb: true },
    { id: 'users', label: 'Usuarios', group: 'Administración', defaultWeb: true },
    { id: 'roles', label: 'Roles', group: 'Administración', defaultWeb: true },
    { id: 'settings', label: 'Configuración', group: 'Administración', defaultWeb: true },
    { id: 'reports', label: 'Reporte de Ventas', group: 'Reportes', defaultWeb: true },
    { id: 'reporte-ventas-mostrador', label: 'Ventas de Mostrador', group: 'Reportes', defaultWeb: true },
];

const STORAGE_KEY = 'web_mode_visible_views';

// Obtener configuración guardada o defaults
function getVisibleViews() {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) return JSON.parse(saved);
    } catch (e) {}
    // Defaults
    return ALL_VIEWS.filter(v => v.defaultWeb).map(v => v.id);
}

function saveVisibleViews(views) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(views));
    // Disparar evento para que el Sidebar se actualice
    window.dispatchEvent(new Event('webmode-config-changed'));
}

const WEB_API_URL_KEY = 'web_api_url';

const WebModeConfig = () => {
    const { showToast } = useApp();
    const [visibleViews, setVisibleViews] = useState(getVisibleViews());
    const [hasChanges, setHasChanges] = useState(false);
    const [webApiUrl, setWebApiUrl] = useState(localStorage.getItem(WEB_API_URL_KEY) || '');
    const [apiUrlSaved, setApiUrlSaved] = useState(true);

    // Agrupar views
    const groups = {};
    ALL_VIEWS.forEach(view => {
        if (!groups[view.group]) groups[view.group] = [];
        groups[view.group].push(view);
    });

    const toggleView = (viewId) => {
        setVisibleViews(prev => {
            const next = prev.includes(viewId)
                ? prev.filter(id => id !== viewId)
                : [...prev, viewId];
            return next;
        });
        setHasChanges(true);
    };

    const toggleGroup = (groupName) => {
        const groupViews = groups[groupName].map(v => v.id);
        const allVisible = groupViews.every(id => visibleViews.includes(id));

        setVisibleViews(prev => {
            if (allVisible) {
                return prev.filter(id => !groupViews.includes(id));
            } else {
                return [...new Set([...prev, ...groupViews])];
            }
        });
        setHasChanges(true);
    };

    const handleSave = () => {
        saveVisibleViews(visibleViews);
        setHasChanges(false);
        showToast('Configuración de modo web guardada', 'success');
    };

    const handleReset = () => {
        const defaults = ALL_VIEWS.filter(v => v.defaultWeb).map(v => v.id);
        setVisibleViews(defaults);
        setHasChanges(true);
    };

    const handleSaveApiUrl = () => {
        const trimmed = webApiUrl.trim();
        if (trimmed) {
            localStorage.setItem(WEB_API_URL_KEY, trimmed);
        } else {
            localStorage.removeItem(WEB_API_URL_KEY);
        }
        apiService.initializeApiUrl();
        setApiUrlSaved(true);
        showToast('URL del API web guardada. Se usará al recargar.', 'success');
    };

    return (
        <div className="webmode-config">
            <div className="webmode-header">
                <div>
                    <h3 style={{ margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Globe size={20} />
                        Configuración Modo Web
                    </h3>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
                        Selecciona qué secciones son visibles cuando el sistema se accede desde un navegador web (sin Electron).
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="outline" onClick={handleReset} size="small">
                        Restaurar defaults
                    </Button>
                    <Button onClick={handleSave} disabled={!hasChanges} size="small">
                        <Save size={16} />
                        Guardar
                    </Button>
                </div>
            </div>

            <div className="webmode-api-url">
                <h4 style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Link size={16} />
                    URL del API (Servidor Nube)
                </h4>
                <p style={{ margin: '0 0 8px', color: '#6b7280', fontSize: 13 }}>
                    URL del API del servidor central/nube. En modo web, el sistema usará esta URL en lugar de la local.
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                        type="text"
                        value={webApiUrl}
                        onChange={(e) => { setWebApiUrl(e.target.value); setApiUrlSaved(false); }}
                        placeholder="https://tuservidor.com/api_pos/reportes/funciones_php/api.php"
                        style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 6,
                            fontSize: 13,
                            fontFamily: 'monospace'
                        }}
                    />
                    <Button onClick={handleSaveApiUrl} disabled={apiUrlSaved} size="small">
                        <Save size={16} />
                        Guardar URL
                    </Button>
                </div>
                {apiService.apiUrl && (
                    <p style={{ margin: '8px 0 0', color: '#9ca3af', fontSize: 12 }}>
                        URL actual en uso: <code>{apiService.apiUrl}</code>
                    </p>
                )}
            </div>

            <div className="webmode-groups">
                {Object.entries(groups).map(([groupName, views]) => {
                    const allVisible = views.every(v => visibleViews.includes(v.id));
                    const someVisible = views.some(v => visibleViews.includes(v.id));

                    return (
                        <div key={groupName} className="webmode-group">
                            <div
                                className="webmode-group-header"
                                onClick={() => toggleGroup(groupName)}
                            >
                                <input
                                    type="checkbox"
                                    checked={allVisible}
                                    ref={el => { if (el) el.indeterminate = someVisible && !allVisible; }}
                                    onChange={() => toggleGroup(groupName)}
                                />
                                <span className="webmode-group-name">{groupName}</span>
                            </div>
                            <div className="webmode-group-items">
                                {views.map(view => (
                                    <label key={view.id} className={`webmode-item ${visibleViews.includes(view.id) ? 'visible' : 'hidden'}`}>
                                        <input
                                            type="checkbox"
                                            checked={visibleViews.includes(view.id)}
                                            onChange={() => toggleView(view.id)}
                                        />
                                        {visibleViews.includes(view.id) ? <Eye size={14} /> : <EyeOff size={14} />}
                                        <span>{view.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Exportar utilidad para que Sidebar la use
export { getVisibleViews, STORAGE_KEY };
export default WebModeConfig;
